import React, { FunctionComponent, useEffect } from 'react';
import { components as SelectComponents, OptionProps, MenuListProps } from 'react-select';
import { List, useListRef } from 'react-window';
import { Replay as ReplayIcon } from '@mui/icons-material';
import { InputAdornment, IconButton } from '@mui/material';
import {
  TeamIcon, FormGroup, Label,
  StyledTextField,
} from './styles';
import {
  ITeamData, IScheduleOption, IValueOption,
  IGameIdFieldProps,
} from './interfaces';
import { ILookupOption } from '../../../state/lookups';
import { IVaultTeam } from '../../../api/vault/teams';
import { IExchangeSchedule } from '../../../api/vault/exchangeSchedule';
import { padGameSeason } from '../../../utils/exchange';

export const scheduleToLabel = ({
  GameDate,
  HomeTeamCode,
  VisitorTeamCode,
  NCAAGameNumber,
  _id,
}: IExchangeSchedule): string => {
  if (_id === null) return 'New Game';
  return `${GameDate} ${HomeTeamCode} vs ${VisitorTeamCode} (${NCAAGameNumber})`;
};

// note: VA-2448 - formatting season displayed to user if hockey
export const valueToOption = (
  value: string,
  pad: boolean = false,
): IValueOption => ({
  label: pad ? padGameSeason(value) : value,
  value,
});

export const valuesToOptions = (
  values: Array<string>,
  pad: boolean = false,
): Array<IValueOption> => values.map((v) => valueToOption(v, pad));

export const scheduleToOption = (
  schedule: IExchangeSchedule,
  teamData: ITeamData,
): IScheduleOption => ({
  label: scheduleToLabel(schedule),
  value: schedule,
  teamData,
});

export const schedulesToOptions = (
  schedules: Array<IExchangeSchedule>,
  teamData: ITeamData,
  allowNewGame: boolean,
  loggedInUsersTeam: IVaultTeam,
  defaultGameType: string,
  season: string,
): Array<IScheduleOption> => {
  const options = schedules.map((item) => scheduleToOption(item, teamData));
  if (allowNewGame) {
    options.unshift({
      label: 'New Game',
      value: {
        _id: null,
        // Optional chaining: allowNewGame is reachable for a user with no team of
        // their own, and this ran before any team was resolved
        League: loggedInUsersTeam?.League,
        Sport: loggedInUsersTeam?.Sport,
        GameType: defaultGameType,
        HomeTeamId: loggedInUsersTeam?._id,
        Season: season,
      } as IExchangeSchedule,
      teamData,
    });
  }
  return options;
};

export const lookupToOption = (
  selectedValue: string,
  options: Array<ILookupOption>,
): ILookupOption => {
  if (selectedValue) return options.find(({ value }) => value === selectedValue);
  return null;
};

const OPTION_HEIGHT = 36;
const MAX_VISIBLE_OPTIONS = 8;

interface RowProps {
  childArray: React.ReactNode[];
}

const VirtualizedRow = ({
  index,
  style,
  childArray,
}: {
  index: number;
  style: React.CSSProperties;
  childArray: React.ReactNode[];
}) => (
  <div style={style}>{childArray[index]}</div>
);

export const VirtualizedMenuList = (props: MenuListProps<any, boolean, any>) => {
  const {
    options, children, maxHeight, getValue,
  } = props;
  const childArray = React.Children.toArray(children);
  const listHeight = Math.min(maxHeight, childArray.length * OPTION_HEIGHT);
  const listRef = useListRef(null);

  const selectedValues = getValue();
  let initialIndex = 0;
  if (selectedValues?.length) {
    const selectedIndex = options.findIndex((opt: any) => opt.value === selectedValues[0]?.value);
    if (selectedIndex > 0) {
      initialIndex = selectedIndex;
    }
  }

  useEffect(() => {
    if (listRef.current && initialIndex > 0) {
      listRef.current.scrollToRow({ index: initialIndex, align: 'start' });
    }
  }, [initialIndex]);

  if (childArray.length === 0) {
    return <div>{children}</div>;
  }

  return (
    <List<RowProps>
      rowCount={childArray.length}
      rowHeight={OPTION_HEIGHT}
      rowComponent={VirtualizedRow}
      rowProps={{ childArray }}
      listRef={listRef}
      style={{ height: listHeight, width: '100%' }}
    />
  );
};

export const TeamOption = (props: OptionProps<any, boolean, any>) => {
  const {
    label,
    isDisabled,
    data: {
      value: {
        TeamImage,
      },
    },
  } = props;

  return !isDisabled ? (
    <SelectComponents.Option {...props}>
      <TeamIcon src={TeamImage} />{label}
    </SelectComponents.Option>
  ) : null;
};

export const GameOption = (props: OptionProps<any, boolean, any>) => {
  const {
    label,
    isDisabled,
    data: {
      teamData: {
        source,
        teamIcons,
      },
      value: schedule,
    },
  } = props;

  let teamIcon: JSX.Element = null;

  if (source && schedule && schedule._id !== null) {
    const { HomeTeamId, VisitorTeamId } = schedule;

    if (source._id === HomeTeamId) {
      teamIcon = (
        <TeamIcon src={teamIcons[VisitorTeamId]} />
      );
    } else if (source._id === VisitorTeamId) {
      teamIcon = (
        <TeamIcon src={teamIcons[HomeTeamId]} />
      );
    }
  }

  return !isDisabled ? (
    <SelectComponents.Option {...props}>
      {teamIcon}{label}
    </SelectComponents.Option>
  ) : null;
};

export const GameIdField: FunctionComponent<IGameIdFieldProps> = (props: IGameIdFieldProps) => {
  const {
    isEditable,
    gameId,
    selectedSeason,
    selectedSport,
    selectedLeague,
    onUpdateGameId,
    onGetGameId,
  } = props;
  return (
    <FormGroup>
      <Label>Game Id</Label>
      <StyledTextField
        value={gameId}
        onChange={(e) => onUpdateGameId(e.target.value)}
        fullWidth
        data-auto="GameID"
        disabled={!isEditable}
        InputProps={{
          endAdornment: isEditable && (
            <InputAdornment position="end">
              <IconButton
                onClick={onGetGameId}
                disabled={!selectedSeason || !selectedSport || !selectedLeague}
                size="large"
              >
                <ReplayIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </FormGroup>
  );
};
